<script lang="ts">
  // Interactive override for the /beta page. This route is NOT in the generated
  // pages.ts, so title/description are set manually. Gated by the runtime beta
  // flag: when OFF it shows an informational block; when ON it renders the
  // participation form (all fields optional, Altcha required) wired to
  // POST /beta-interest. German copy throughout.
  import { onMount } from "svelte";
  import Header from "./Header.svelte";
  import Footer from "./Footer.svelte";
  import { applyBehaviors } from "./behaviors";
  import { betaEnabled } from "./beta";
  import { altchaChallengeUrl, submitBetaInterest } from "./api";

  const TITLE = "wiederdabei — Beta-Phase";
  const DESCRIPTION =
    "Beta-Phase: Wir hosten den Server gerade kostenlos und suchen Tester:innen. Mach mit und gib uns Feedback.";

  let main: HTMLElement;
  let cleanup: (() => void) | null = null;

  // Form fields — all optional.
  let name = "";
  let email = "";
  let situation = "";
  let expertise = "";
  let dateFrom = "";
  let dateTo = "";
  let hp = ""; // honeypot — kept empty by humans

  let altchaReady = false;
  let submitting = false;
  let done = false;
  let errorMsg: string | null = null;

  let formEl: HTMLFormElement;

  // Map backend error tokens to German user-facing messages.
  function describeError(token: string | undefined): string {
    switch (token) {
      case "beta_disabled":
        return "Die Beta-Phase ist derzeit nicht aktiv.";
      case "altcha_invalid":
        return "Die Sicherheitsprüfung ist fehlgeschlagen. Bitte erneut versuchen.";
      case "rate_limited":
        return "Zu viele Anfragen. Bitte später erneut versuchen.";
      case "email_invalid":
        return "Bitte eine gültige E-Mail-Adresse angeben.";
      case "name_too_long":
      case "situation_too_long":
      case "expertise_too_long":
      case "date_invalid":
        return "Bitte Eingaben prüfen.";
      case "recipient_not_configured":
        return "Der Versand ist derzeit nicht verfügbar. Bitte später erneut versuchen.";
      case "send_failed":
        return "Senden fehlgeschlagen. Bitte später erneut versuchen.";
      case "network":
        return "Netzwerkfehler. Bitte Verbindung prüfen und erneut versuchen.";
      default:
        return "Senden fehlgeschlagen. Bitte später erneut versuchen.";
    }
  }

  // Read the Altcha widget's hidden input value (the solution payload).
  function readAltcha(): string {
    const input = formEl?.querySelector<HTMLInputElement>(
      'input[name="altcha"]',
    );
    return input?.value ?? "";
  }

  async function handleSubmit() {
    errorMsg = null;
    // All content fields are optional — only the Altcha solution is required.
    const altcha = readAltcha();
    if (!altcha) {
      errorMsg = "Bitte die Sicherheitsprüfung abschließen (Häkchen setzen).";
      return;
    }
    submitting = true;
    const res = await submitBetaInterest({
      name: name.trim(),
      email: email.trim(),
      situation: situation.trim(),
      expertise: expertise.trim(),
      dateFrom: dateFrom.trim(),
      dateTo: dateTo.trim(),
      altcha,
      hp,
    });
    submitting = false;
    if (res.ok) {
      done = true;
    } else {
      errorMsg = describeError(res.error);
    }
  }

  onMount(() => {
    // Register the <altcha-widget> custom element + German translations.
    // Sequential: core must run first to create globalThis.$altcha, which
    // altcha/i18n/de checks before registering German strings. Parallel
    // (Promise.all) races and can skip the registration → English fallback.
    import("altcha")
      .then(() => import("altcha/i18n/de"))
      .then(() => (altchaReady = true));
    document.title = TITLE;
    let desc = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    desc.content = DESCRIPTION;
    window.scrollTo({ top: 0, behavior: "auto" });
    if (main) cleanup = applyBehaviors(main);
    return () => cleanup?.();
  });
</script>

<Header active="" />
<main bind:this={main}>
  <div class="wrap" style="padding-block:clamp(28px,4vw,44px) 0">
    <div class="crumb">
      <a href="/">Start</a><span class="sep">›</span><span class="here">Beta</span>
    </div>
  </div>
  <section class="section" style="padding-top:clamp(22px,3vw,34px)">
    <div class="wrap" style="max-width:880px">
      <span class="eyebrow">Beta-Phase</span>
      <h1 class="title" style="font-size:clamp(30px,4.2vw,50px); margin:14px 0 18px">
        Bei der Beta mitmachen
      </h1>
      <div class="prose" style="max-width:none; margin-top:10px">
        {#if !$betaEnabled}
          <div class="callout" role="status" data-testid="beta-inactive">
            <p>
              <strong>Die Beta-Phase ist derzeit nicht aktiv.</strong>
              Schau gern später wieder vorbei — oder schreib uns direkt über das
              <a href="/kontakt">Kontaktformular</a>.
            </p>
          </div>
        {:else if done}
          <div class="callout" role="status" data-testid="beta-success">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"></path></svg>
            <p>
              <strong>Danke!</strong> Wir melden uns zur Beta-Teilnahme.
            </p>
          </div>
        {:else}
          <p>
            Während der Beta hosten wir den Server für euch
            <strong>komplett kostenlos</strong>. Wir wollen wiederdabei mit
            echten Nutzer:innen verbessern und freuen uns über euer Feedback.
            Alle Felder sind freiwillig — sag uns einfach, was für euch passt.
          </p>

          <form
            class="wd-form"
            bind:this={formEl}
            on:submit|preventDefault={handleSubmit}
            novalidate
            data-testid="beta-form"
          >
            {#if errorMsg}
              <p class="wd-form__error" role="alert" data-testid="beta-error">
                {errorMsg}
              </p>
            {/if}

            <label class="wd-field">
              <span>Name</span>
              <input type="text" bind:value={name} maxlength="200" autocomplete="name" />
            </label>

            <label class="wd-field">
              <span>E-Mail</span>
              <input type="email" bind:value={email} maxlength="320" autocomplete="email" />
            </label>

            <label class="wd-field">
              <span>Wie sieht eure Situation aus und wie möchtet ihr wiederdabei testen/einsetzen?</span>
              <textarea bind:value={situation} rows="5" maxlength="5000"></textarea>
            </label>

            <label class="wd-field">
              <span>Wie ist eure technische Expertise? (damit wir den passenden Support einplanen)</span>
              <textarea bind:value={expertise} rows="4" maxlength="5000"></textarea>
            </label>

            <div class="wd-dates">
              <label class="wd-field">
                <span>Zeitraum von</span>
                <input type="date" bind:value={dateFrom} />
              </label>
              <label class="wd-field">
                <span>bis</span>
                <input type="date" bind:value={dateTo} />
              </label>
            </div>

            <!-- Honeypot: visually hidden + off-screen; bots fill it, humans don't. -->
            <div class="wd-hp" aria-hidden="true">
              <label>
                Bitte dieses Feld leer lassen
                <input
                  type="text"
                  bind:value={hp}
                  tabindex="-1"
                  autocomplete="off"
                />
              </label>
            </div>

            <div class="wd-altcha">
              {#if altchaReady}
                <!-- svelte-ignore a11y-unknown-element -->
                <altcha-widget challenge={altchaChallengeUrl()} language="de"></altcha-widget>
              {:else}
                <p class="wd-form__hint">Sicherheitsprüfung wird geladen…</p>
              {/if}
            </div>

            <button
              class="btn btn--primary"
              type="submit"
              disabled={submitting}
              data-testid="beta-submit"
            >
              {submitting ? "Senden…" : "Mitmachen"}
            </button>

            <p class="wd-form__hint">
              Deine Angaben werden ausschließlich per E-Mail an uns gesendet. Zum
              Schutz vor Spam nutzt das Formular eine selbstgehostete
              Altcha-Sicherheitsprüfung.
            </p>
          </form>
        {/if}
      </div>
    </div>
  </section>
</main>
<Footer />

<style>
  .wd-form {
    display: grid;
    gap: 16px;
    max-width: 560px;
    margin-top: 18px;
  }
  .wd-field {
    display: grid;
    gap: 6px;
  }
  .wd-field > span {
    font-weight: 600;
    color: var(--ink, inherit);
  }
  .wd-field input,
  .wd-field textarea {
    width: 100%;
    padding: 11px 13px;
    border: 1px solid var(--line, #d4d8de);
    border-radius: 10px;
    background: var(--surface, #fff);
    color: var(--ink, #1a1d22);
    font: inherit;
  }
  .wd-field input:focus,
  .wd-field textarea:focus {
    outline: none;
    border-color: var(--primary, #2f6df6);
    box-shadow: 0 0 0 3px rgba(47, 109, 246, 0.18);
  }
  .wd-dates {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  @media (max-width: 480px) {
    .wd-dates {
      grid-template-columns: 1fr;
    }
  }
  .wd-altcha {
    margin-block: 4px;
  }
  .wd-form__error {
    color: #b00020;
    background: rgba(176, 0, 32, 0.08);
    border: 1px solid rgba(176, 0, 32, 0.25);
    padding: 10px 12px;
    border-radius: 10px;
  }
  .wd-form__hint {
    color: var(--muted, #5a6270);
    font-size: 14px;
  }
  .wd-form .btn {
    justify-self: start;
  }
  /* Honeypot: removed from layout + invisible, still focusable by bots only. */
  .wd-hp {
    position: absolute !important;
    left: -9999px !important;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }
</style>
